/**
 * Recipe scaling + shopping-list math (pure, no I/O).
 *
 * Ingredient quantities in the wild are messy strings — "2", "1 1/2",
 * "1/4", "2-3", "to taste", "a pinch".  We handle the numeric cases
 * (scale by a factor) and pass through everything else untouched so
 * we never lie to a user.
 */

/** Try to parse an ingredient quantity string into a number.
 * Accepts: "2", "1.5", "1/2", "1 1/2", ".75". Returns null when the
 * quantity is non-numeric (e.g. "to taste").
 */
export function parseQuantityToken(tok) {
  if (tok == null) return null;
  const s = String(tok).trim();
  if (!s) return null;
  // Mixed fraction: "1 1/2"
  const mixed = s.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (mixed) {
    const whole = parseInt(mixed[1], 10);
    const num = parseInt(mixed[2], 10);
    const den = parseInt(mixed[3], 10);
    if (den) return whole + num / den;
  }
  // Simple fraction: "3/4"
  const frac = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (frac) {
    const num = parseInt(frac[1], 10);
    const den = parseInt(frac[2], 10);
    if (den) return num / den;
  }
  // Decimal / integer
  if (/^-?\d*\.?\d+$/.test(s)) return parseFloat(s);
  return null;
}

/** Format a scaled number back to a friendly string. */
export function formatScaled(n) {
  if (n == null || Number.isNaN(n)) return "";
  if (Math.abs(n) < 0.001) return "";
  // Integers stay integers.
  if (Math.abs(n - Math.round(n)) < 0.01) return String(Math.round(n));
  // Prefer common fractions for values ≤ 4 and clean fractional parts.
  if (n > 0 && n < 4) {
    const whole = Math.floor(n);
    const rem = n - whole;
    const halves = Math.round(rem * 8) / 8; // eighths
    const fracMap = {
      0.125: "1/8", 0.25: "1/4", 0.375: "3/8", 0.5: "1/2",
      0.625: "5/8", 0.75: "3/4", 0.875: "7/8",
    };
    if (fracMap[halves] !== undefined) {
      return whole > 0 ? `${whole} ${fracMap[halves]}` : fracMap[halves];
    }
  }
  return n.toFixed(2).replace(/\.?0+$/, "");
}

/** Scale a single quantity string by a factor. Ranges like "2-3" are
 * scaled on both bounds; free-form strings are returned untouched.
 */
export function scaleQuantity(qty, factor) {
  if (qty == null) return "";
  const s = String(qty).trim();
  if (!s) return "";
  // Range "2-3"
  const range = s.match(/^(\S+?)\s*[-–—]\s*(\S+)$/);
  if (range) {
    const lo = parseQuantityToken(range[1]);
    const hi = parseQuantityToken(range[2]);
    if (lo != null && hi != null) {
      return `${formatScaled(lo * factor)}–${formatScaled(hi * factor)}`;
    }
  }
  const n = parseQuantityToken(s);
  if (n != null) return formatScaled(n * factor);
  return s; // "to taste", "pinch", etc.
}

/** Scale every ingredient row on a recipe object. Returns a new array. */
export function scaleIngredients(ingredients, factor) {
  if (!Array.isArray(ingredients)) return [];
  return ingredients.map((row) => ({
    ...row,
    quantity: scaleQuantity(row.quantity, factor),
  }));
}


// ---------------- Shopping list (localStorage-backed) ----------------
const SHOP_KEY = "wofy_shopping_list_v1";

/** Shape of the persisted shopping cart:
 * {
 *   recipes: { [slug]: { title, servings_base, servings_target, cover_photo, added_at, ingredients:[{name,quantity,unit,optional}] } }
 * }
 */
export function loadShoppingList() {
  try {
    const raw = localStorage.getItem(SHOP_KEY);
    if (!raw) return { recipes: {} };
    return JSON.parse(raw) || { recipes: {} };
  } catch { return { recipes: {} }; }
}

export function saveShoppingList(list) {
  try { localStorage.setItem(SHOP_KEY, JSON.stringify(list)); } catch {}
  // Fire an event so other components (like a header badge) can react.
  try { window.dispatchEvent(new Event("wofy-shopping-list-updated")); } catch {}
}

export function addRecipeToShoppingList(recipe, servingsTarget) {
  const list = loadShoppingList();
  list.recipes = list.recipes || {};
  list.recipes[recipe.slug] = {
    slug: recipe.slug,
    title: recipe.title,
    cover_photo: recipe.cover_photo,
    servings_base: recipe.servings || 4,
    servings_target: servingsTarget || recipe.servings || 4,
    ingredients: (recipe.ingredients || []).map((i) => ({
      name: i.name, quantity: i.quantity, unit: i.unit, optional: !!i.optional,
    })),
    added_at: new Date().toISOString(),
  };
  saveShoppingList(list);
  return list;
}

export function removeRecipeFromShoppingList(slug) {
  const list = loadShoppingList();
  if (list.recipes && list.recipes[slug]) {
    delete list.recipes[slug];
    saveShoppingList(list);
  }
  return list;
}

export function updateServingsInShoppingList(slug, target) {
  const list = loadShoppingList();
  if (list.recipes && list.recipes[slug]) {
    list.recipes[slug].servings_target = target;
    saveShoppingList(list);
  }
  return list;
}

export function clearShoppingList() {
  saveShoppingList({ recipes: {} });
}

export function shoppingListSize(list) {
  return Object.keys((list && list.recipes) || {}).length;
}

/** Aggregate ingredients across every recipe in the cart, applying the
 * per-recipe scale factor. Same (name lowercase, unit lowercase) rows
 * merge; different units stay separate rows (we don't invent
 * cross-unit conversions).
 */
export function aggregateShoppingList(list) {
  const bucket = new Map();
  const orphans = []; // non-scalable qty rows we can't sum
  for (const rec of Object.values((list && list.recipes) || {})) {
    const base = rec.servings_base || 1;
    const target = rec.servings_target || base;
    const factor = target / base;
    for (const ing of rec.ingredients || []) {
      const nameKey = (ing.name || "").trim().toLowerCase();
      const unitKey = (ing.unit || "").trim().toLowerCase();
      if (!nameKey) continue;
      const scaled = scaleQuantity(ing.quantity, factor);
      const numeric = parseQuantityToken(ing.quantity);
      if (numeric == null) {
        orphans.push({
          name: ing.name,
          quantity: ing.quantity,
          unit: ing.unit,
          optional: ing.optional,
          from: [rec.title],
        });
        continue;
      }
      const key = `${nameKey}|${unitKey}`;
      const scaledN = numeric * factor;
      const prev = bucket.get(key);
      if (prev) {
        prev.numeric += scaledN;
        prev.from.push(rec.title);
        prev.optional = prev.optional && !!ing.optional;
      } else {
        bucket.set(key, {
          name: ing.name,
          unit: ing.unit,
          numeric: scaledN,
          optional: !!ing.optional,
          from: [rec.title],
        });
      }
      // scaled var used implicitly via numeric; keep placeholder
      void scaled;
    }
  }
  const rows = Array.from(bucket.values()).map((r) => ({
    name: r.name,
    unit: r.unit,
    quantity: formatScaled(r.numeric),
    optional: r.optional,
    from: r.from,
  }));
  // Alphabetical for the shopper's eye.
  rows.sort((a, b) => a.name.localeCompare(b.name));
  orphans.sort((a, b) => a.name.localeCompare(b.name));
  return { rows, orphans };
}

/** Aisle categorisation for the shopping list — a lightweight keyword heuristic.
 *
 * The goal isn't to be a perfect grocery-store map; it's to turn a flat
 * checklist into an aisle-by-aisle route. Keywords are matched
 * case-insensitively against the ingredient name; the first bucket that
 * matches wins. Unknown items land in "Other".
 */
export const AISLE_ORDER = [
  "Produce",
  "Meat & Fish",
  "Dairy & Eggs",
  "Bakery",
  "Dry Goods & Pantry",
  "Spices & Baking",
  "Frozen",
  "Beverages",
  "Household",
  "Other",
];

const AISLE_KEYWORDS = {
  Produce: [
    "lettuce","spinach","kale","arugula","cabbage","carrot","celery","onion","garlic","shallot","scallion","leek",
    "tomato","cucumber","zucchini","squash","pumpkin","pepper","chili","chile","eggplant","aubergine","broccoli","cauliflower",
    "asparagus","mushroom","potato","sweet potato","yam","beet","radish","turnip","parsnip","corn",
    "apple","banana","orange","lemon","lime","grape","pear","peach","plum","mango","pineapple","strawberry","blueberry","raspberry","blackberry","cherry",
    "avocado","cilantro","parsley","basil","mint","thyme","rosemary","sage","chive","dill","oregano","tarragon","bay leaf",
    "ginger","turmeric root","fresh herb","salad","greens","fruit","vegetable","herb",
  ],
  "Meat & Fish": [
    "chicken","turkey","duck","beef","steak","pork","bacon","ham","sausage","lamb","veal","mutton","goat",
    "salmon","tuna","cod","tilapia","trout","haddock","shrimp","prawn","crab","lobster","mussel","clam","oyster","scallop","squid","octopus",
    "fish","meat","mince","ground beef","ground turkey","ground pork","chorizo","pepperoni","prosciutto","salami",
  ],
  "Dairy & Eggs": [
    "milk","cream","half-and-half","half and half","yogurt","yoghurt","butter","ghee","cheese","parmesan","mozzarella","cheddar",
    "feta","ricotta","cottage cheese","cream cheese","sour cream","buttermilk","egg","eggs","kefir","paneer",
  ],
  Bakery: [
    "bread","baguette","roll","bun","bagel","croissant","pita","tortilla","naan","pizza dough","pastry","cake","muffin",
  ],
  "Dry Goods & Pantry": [
    "rice","pasta","noodle","spaghetti","penne","macaroni","couscous","quinoa","barley","oat","oatmeal",
    "flour","bread crumb","breadcrumb","cornmeal","polenta","semolina","cornstarch","starch",
    "bean","lentil","chickpea","garbanzo","black bean","kidney bean","cannellini","pinto",
    "canned tomato","tomato sauce","tomato paste","broth","stock","coconut milk","canned",
    "oil","olive oil","vegetable oil","canola","sesame oil","coconut oil",
    "vinegar","soy sauce","fish sauce","hoisin","worcestershire","sriracha","hot sauce","ketchup","mustard","mayonnaise","mayo","tahini","peanut butter","honey","maple syrup","molasses","jam","jelly",
    "sugar","brown sugar","confectioner","powdered sugar","salt","kosher salt","sea salt",
  ],
  "Spices & Baking": [
    "pepper","black pepper","white pepper","cumin","coriander","paprika","cinnamon","nutmeg","clove","cardamom",
    "allspice","fennel seed","mustard seed","chili powder","curry powder","garam masala","turmeric","saffron","vanilla",
    "baking soda","baking powder","yeast","cocoa","chocolate chip","chocolate","cinnamon stick",
  ],
  Frozen: [
    "frozen","ice cream","gelato","sorbet","ice",
  ],
  Beverages: [
    "water","juice","soda","tea","coffee","espresso","milk alternative","almond milk","oat milk","soy milk","wine","beer","stock cube","broth cube",
  ],
  Household: [
    "paper towel","napkin","foil","parchment","plastic wrap","dish soap","sponge","trash bag","toothpick","skewer","candle",
  ],
};

// Precompute lowercase set for cheap lookup — longest keywords first so
// "sweet potato" beats "potato" when both are present.
const AISLE_LOOKUP = Object.entries(AISLE_KEYWORDS).flatMap(([aisle, kws]) =>
  kws.map((k) => ({ aisle, kw: k.toLowerCase(), len: k.length })),
).sort((a, b) => b.len - a.len);

export function aisleForIngredient(name) {
  if (!name) return "Other";
  const n = String(name).toLowerCase();
  for (const { aisle, kw } of AISLE_LOOKUP) {
    if (n.includes(kw)) return aisle;
  }
  return "Other";
}

/** Group aggregated rows + orphans by aisle in `AISLE_ORDER`. */
export function groupByAisle(agg) {
  const groups = new Map(AISLE_ORDER.map((a) => [a, { rows: [], orphans: [] }]));
  for (const r of agg.rows || []) {
    const a = aisleForIngredient(r.name);
    groups.get(a).rows.push(r);
  }
  for (const o of agg.orphans || []) {
    const a = aisleForIngredient(o.name);
    groups.get(a).orphans.push(o);
  }
  // Return an ordered array, dropping empty aisles.
  return AISLE_ORDER
    .map((aisle) => ({ aisle, ...groups.get(aisle) }))
    .filter((g) => g.rows.length > 0 || g.orphans.length > 0);
}


/** Render aggregated rows into a plain-text shopping list (for
 * clipboard copy / print / share).
 */
export function shoppingListToText(list) {
  const agg = aggregateShoppingList(list);
  const groups = groupByAisle(agg);
  const lines = ["Shopping list — Social Pulse", ""];
  for (const g of groups) {
    lines.push(`## ${g.aisle}`);
    for (const r of g.rows) {
      const qty = [r.quantity, r.unit].filter(Boolean).join(" ");
      lines.push(`  • ${qty ? qty + " " : ""}${r.name}${r.optional ? " (optional)" : ""}`);
    }
    for (const o of g.orphans) {
      const qty = [o.quantity, o.unit].filter(Boolean).join(" ");
      lines.push(`  • ${qty ? qty + " — " : ""}${o.name}`);
    }
    lines.push("");
  }
  lines.push("Sources:");
  for (const rec of Object.values((list && list.recipes) || {})) {
    lines.push(`  · ${rec.title} — ${rec.servings_target} servings`);
  }
  return lines.join("\n");
}
